import { useEffect, useMemo, useState } from 'react';
import { Edit3, HeartHandshake, Link2, Network, Save, Trash2, X } from 'lucide-react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import type { Relationship } from './types';
import { POLONIA_CREST } from './clubIdentity';

function relationClass(score: number) {
  if (score >= 60) return 'strong';
  if (score >= 30) return 'medium';
  return 'weak';
}

function relationLabel(score: number) {
  if (score >= 60) return 'Strong';
  if (score >= 30) return 'Medium';
  return 'Weak';
}

export function HarmonyNetwork() {
  const { t } = useI18n();
  const {
    players,
    relationships,
    loading,
    error,
    saveRelationship,
    deleteRelationship,
  } = useData();

  const [playerA, setPlayerA] = useState('');
  const [playerB, setPlayerB] = useState('');
  const [score, setScore] = useState(60);
  const [notes, setNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const playerMap = useMemo(
    () => new Map(players.map((player) => [player.id, player])),
    [players],
  );

  const sortedPlayers = useMemo(
    () => players.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [players],
  );

  const average = relationships.length
    ? Math.round(
        relationships.reduce((sum, relationship) => sum + Number(relationship.score ?? 0), 0) /
          relationships.length,
      )
    : 0;

  const currentClass = relationClass(score);

  useEffect(() => {
    if (playerA && playerB && playerA === playerB) setPlayerB('');
  }, [playerA, playerB]);

  function resetEditor() {
    setPlayerA('');
    setPlayerB('');
    setScore(60);
    setNotes('');
    setEditingId(null);
    setMessage('');
  }

  function editRelationship(relationship: Relationship) {
    setPlayerA(relationship.player_a);
    setPlayerB(relationship.player_b);
    setScore(Number(relationship.score ?? 0));
    setNotes(relationship.notes ?? '');
    setEditingId(relationship.id ?? null);
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submitRelationship() {
    setSaving(true);
    setMessage('');
    const result = await saveRelationship({
      playerA,
      playerB,
      score,
      notes,
    });
    setSaving(false);
    setMessage(result.message);
    if (result.ok) resetEditor();
  }

  async function removeRelationship(relationship: Relationship) {
    if (!relationship.id) return;
    const first = playerMap.get(relationship.player_a)?.name ?? 'Player A';
    const second = playerMap.get(relationship.player_b)?.name ?? 'Player B';
    if (!window.confirm(`Remove chemistry connection between ${first} and ${second}?`)) return;

    setSaving(true);
    const result = await deleteRelationship(relationship.id);
    setSaving(false);
    setMessage(result.message);
    if (editingId === relationship.id) resetEditor();
  }

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="harmony-page">
      <section className="metal-card module-heading harmony-heading">
        <div>
          <p className="eyebrow">SQUAD DYNAMICS</p>
          <h1>{t.harmony}</h1>
          <p className="muted">{t.harmonySubtitle}</p>
        </div>
        <div className="chemistry-score">
          <HeartHandshake size={28} />
          <strong>{average}</strong>
          <span>/100</span>
        </div>
      </section>

      <section className="metal-card harmony-editor">
        <div className="harmony-editor-heading">
          <div>
            <p className="eyebrow">MANUAL CHEMISTRY EDITOR</p>
            <h2>{editingId ? 'Edit player connection' : 'Create player connection'}</h2>
            <p className="muted">
              Choose two players and set one shared chemistry value. A↔B is always one relationship.
            </p>
          </div>
          <div className={`harmony-score-preview ${currentClass}`}>
            <strong>{score}</strong>
            <span>{relationLabel(score)}</span>
          </div>
        </div>

        <div className="harmony-editor-grid">
          <label>
            <span>Player A</span>
            <select value={playerA} onChange={(event) => setPlayerA(event.target.value)}>
              <option value="">Select player</option>
              {sortedPlayers.map((player) => (
                <option key={player.id} value={player.id}>{player.name} · {player.position}</option>
              ))}
            </select>
          </label>

          <div className="harmony-pair-symbol" aria-hidden="true">
            <Link2 size={22} />
            <span>↔</span>
          </div>

          <label>
            <span>Player B</span>
            <select value={playerB} onChange={(event) => setPlayerB(event.target.value)}>
              <option value="">Select player</option>
              {sortedPlayers
                .filter((player) => player.id !== playerA)
                .map((player) => (
                  <option key={player.id} value={player.id}>{player.name} · {player.position}</option>
                ))}
            </select>
          </label>
        </div>

        <div className="harmony-slider-panel">
          <div className="harmony-slider-top">
            <strong>Chemistry</strong>
            <span className={`chemistry-number ${currentClass}`}>{score}/100</span>
          </div>
          <input
            className={`harmony-slider ${currentClass}`}
            type="range"
            min="0"
            max="100"
            step="1"
            value={score}
            onChange={(event) => setScore(Number(event.target.value))}
          />
          <div className="harmony-thresholds">
            <span className="weak">0–29 · Weak</span>
            <span className="medium">30–59 · Medium</span>
            <span className="strong">60–100 · Strong</span>
          </div>
          <div className="harmony-quick-values">
            {[20, 40, 60, 80, 100].map((value) => (
              <button
                type="button"
                key={value}
                className={score === value ? 'active' : ''}
                onClick={() => setScore(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        <label className="harmony-note-field">
          <span>Coach note · optional</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Example: Strong understanding on the left side; combines naturally under pressure."
            rows={3}
          />
        </label>

        <div className="harmony-editor-actions">
          <button
            className="gold-button"
            type="button"
            disabled={saving || !playerA || !playerB || playerA === playerB}
            onClick={() => void submitRelationship()}
          >
            <Save size={17} />
            {saving ? 'Saving…' : editingId ? 'Update chemistry' : 'Save connection'}
          </button>
          {(editingId || playerA || playerB || notes) && (
            <button className="steel-button" type="button" disabled={saving} onClick={resetEditor}>
              <X size={16} />
              Cancel
            </button>
          )}
          {message && <span className="harmony-editor-message">{message}</span>}
        </div>
      </section>

      <section className="metal-card harmony-legend">
        <span><i className="legend-dot strong" /> {t.strongLink} · 60–100</span>
        <span><i className="legend-dot medium" /> {t.mediumLink} · 30–59</span>
        <span><i className="legend-dot weak" /> {t.weakLink} · 0–29</span>
      </section>

      <section className="relationship-grid">
        {relationships
          .slice()
          .sort((a, b) => Number(b.score ?? 0) - Number(a.score ?? 0))
          .map((relationship, index) => {
            const first = playerMap.get(relationship.player_a);
            const second = playerMap.get(relationship.player_b);
            const relationshipScore = Number(relationship.score ?? 0);
            const klass = relationClass(relationshipScore);
            return (
              <article
                className={`metal-card relationship-card ${klass} ${editingId === relationship.id ? 'is-editing' : ''}`}
                key={relationship.id ?? index}
              >
                <div className="relationship-player">
                  {first?.photo_url
                    ? <img src={first.photo_url} alt="" />
                    : <img src={POLONIA_CREST} alt="" />}
                  <strong>{first?.name || t.unknownPlayer}</strong>
                  <small>{first?.position}</small>
                </div>

                <div className="relationship-link">
                  <span>{relationshipScore}</span>
                  <i className={klass} />
                  <small>{relationLabel(relationshipScore)}</small>
                </div>

                <div className="relationship-player">
                  {second?.photo_url
                    ? <img src={second.photo_url} alt="" />
                    : <img src={POLONIA_CREST} alt="" />}
                  <strong>{second?.name || t.unknownPlayer}</strong>
                  <small>{second?.position}</small>
                </div>

                {relationship.notes && <p>{relationship.notes}</p>}

                <div className="relationship-actions">
                  <button className="steel-button" type="button" onClick={() => editRelationship(relationship)}>
                    <Edit3 size={15} />
                    Edit
                  </button>
                  <button
                    className="steel-button relationship-delete"
                    type="button"
                    disabled={saving}
                    onClick={() => void removeRelationship(relationship)}
                  >
                    <Trash2 size={15} />
                    Remove
                  </button>
                </div>
              </article>
            );
          })}
      </section>

      {!relationships.length && (
        <section className="metal-card empty-panel harmony-empty">
          <Network size={40} />
          <h3>No chemistry connections yet</h3>
          <p>Create the first connection above. The same pair never needs to be entered twice.</p>
        </section>
      )}
    </div>
  );
}